import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MonthlyPaymentVolume, useSetMonthlyVolume } from '../../../../queries/pxtraCard';
import { ContentDialog } from '../dialogs/contentDialog';
import { onboardingErrorMessage } from './errorMessage';

export const MONTHLY_VOLUMES: MonthlyPaymentVolume[] = [
  'less_than_10000',
  'between_10000_and_50000',
  'between_50000_and_100000',
  'more_than_100000'
];

type Props = {
  companyId: string;
  current: MonthlyPaymentVolume | '';
  open: boolean;
  onClose: () => void;
};

/**
 * Behind a dialog rather than inline on the page: Swan monitors the account
 * against this figure, so it is not something to change by brushing past a
 * select.
 */
export const MonthlyVolumeDialog: FC<Props> = ({ companyId, current, open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [volume, setVolume] = useState<MonthlyPaymentVolume | ''>(current);
  const { mutate: save, isPending } = useSetMonthlyVolume(companyId);

  const submit = () => {
    if (!volume) return;

    save(volume, {
      onSuccess: () => {
        enqueueSnackbar(t('pxtraCard:settingsSaved'), { variant: 'success' });
        onClose();
      },
      onError: (error) =>
        enqueueSnackbar(onboardingErrorMessage(error) ?? t('pxtraCard:onboardingActionFailed'), {
          variant: 'error'
        })
    });
  };

  return (
    <ContentDialog
      open={open}
      width="480px"
      title={t('pxtraCard:settingsMonthlyVolume')}
      onClose={onClose}
      secondaryButton={t('common:CANCEL')}
      onSecondaryButtonClick={onClose}
      primaryButton={t('pxtraCard:taxIdSave')}
      primaryButtonDisabled={!volume || volume === current || isPending}
      onPrimaryButtonClick={submit}
    >
      <div>
        {MONTHLY_VOLUMES.map((option) => (
          <label key={option} className="mb-1 flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="monthlyVolume"
              checked={volume === option}
              onChange={() => setVolume(option)}
            />
            {t(`pxtraCard:volume_${option}`)}
          </label>
        ))}

        <p className="m-0 mt-3 text-xs text-grey-600">{t('pxtraCard:settingsMonthlyVolumeHint')}</p>
      </div>
    </ContentDialog>
  );
};
